import { useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Star, ChevronLeft, ChevronRight, Quote } from "lucide-react";
import { reviewsData } from "@/i18n";

const PER_PAGE = 3;

// Stable keys for the 5 stars (avoid array index)
const STAR_KEYS = ["s1", "s2", "s3", "s4", "s5"];

function ReviewCard({ review, lang }) {
  return (
    <motion.article
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.4 }}
      className="bg-bn-bg border border-bn-border p-7 flex flex-col"
      data-testid={`review-card-${review.id}`}
    >
      <Quote className="text-bn-primary mb-4" size={32} />

      <div className="flex gap-0.5 mb-3">
        {STAR_KEYS.map((k, idx) => (
          <Star
            key={k}
            size={16}
            className={idx < review.rating ? "fill-bn-accent text-bn-accent" : "text-bn-border"}
          />
        ))}
      </div>

      <p className="font-body text-bn-text leading-relaxed mb-5 flex-1">
        {review.text[lang]}
      </p>

      <div className="pt-4 border-t border-bn-border">
        <p className="font-display text-bn-secondary text-base">{review.name}</p>
        <p className="font-body text-xs text-bn-text-muted mt-0.5">
          {review.role} · {review.when[lang]}
        </p>
      </div>
    </motion.article>
  );
}

export default function Reviews({ t, lang }) {
  const [page, setPage] = useState(0);
  const totalPages = Math.ceil(reviewsData.length / PER_PAGE);
  const dotKeys = useMemo(
    () => Array.from({ length: totalPages }, (_, i) => `page-${i}`),
    [totalPages]
  );

  const next = () => setPage((p) => (p + 1) % totalPages);
  const prev = () => setPage((p) => (p - 1 + totalPages) % totalPages);

  const visible = reviewsData.slice(page * PER_PAGE, page * PER_PAGE + PER_PAGE);

  return (
    <section
      id="reviews"
      data-testid="reviews-section"
      className="relative py-24 md:py-32 bg-bn-surface overflow-hidden"
    >
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12 md:mb-16">
          <div className="max-w-2xl">
            <p className="font-body text-bn-primary text-sm uppercase tracking-[0.3em] mb-4">
              — {t.reviews.kicker}
            </p>
            <h2
              className="font-display text-bn-secondary text-4xl md:text-5xl lg:text-6xl leading-[1.05] mb-4"
              data-testid="reviews-title"
            >
              {t.reviews.title}
            </h2>
            <p className="font-body text-bn-text-muted text-base md:text-lg">
              {t.reviews.subtitle}
            </p>
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={prev}
              data-testid="reviews-prev"
              aria-label="prev"
              className="w-12 h-12 border-2 border-bn-secondary text-bn-secondary hover:bg-bn-secondary hover:text-bn-bg transition-colors flex items-center justify-center"
            >
              <ChevronLeft size={22} />
            </button>
            <button
              onClick={next}
              data-testid="reviews-next"
              aria-label="next"
              className="w-12 h-12 border-2 border-bn-secondary bg-bn-secondary text-bn-bg hover:bg-bn-primary hover:border-bn-primary transition-colors flex items-center justify-center"
            >
              <ChevronRight size={22} />
            </button>
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-5 md:gap-6">
          <AnimatePresence mode="wait">
            {visible.map((r) => (
              <ReviewCard key={`${page}-${r.id}`} review={r} lang={lang} />
            ))}
          </AnimatePresence>
        </div>

        <div className="mt-10 flex items-center justify-center gap-2">
          {dotKeys.map((k, i) => (
            <button
              key={k}
              onClick={() => setPage(i)}
              data-testid={`reviews-dot-${i}`}
              aria-label={`page ${i + 1}`}
              className={`h-2 transition-all ${
                page === i ? "w-8 bg-bn-primary" : "w-2 bg-bn-border"
              }`}
            />
          ))}
        </div>

        <p className="text-center mt-8 font-body text-sm text-bn-text-muted uppercase tracking-wider">
          {t.reviews.source}
        </p>
      </div>
    </section>
  );
}
